import {
  CreateEquipmentMakeMutation,
  CreateEquipmentMakeMutationVariables,
  EquipmentMake,
  EquipmentMakeCreateInput,
  EquipmentMakeQuery,
  EquipmentMakeQueryVariables,
  EquipmentMakeUpdateInput,
  UpdateEquipmentMakeMutation,
  UpdateEquipmentMakeMutationVariables,
} from '__generated__/graphql';
import gql from 'graphql-tag';
import { useOrganization } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { orderBy } from 'lodash';

export default function useEquipmentMakes() {
  const client = useGraphQLClient();
  const pageAll = useGraphQLPaging();
  const org = useOrganization();

  const getEquipmentMakes = async () => {
    try {
      const equipmentMakes = await pageAll<EquipmentMakeQuery, EquipmentMakeQueryVariables>(
        {
          document: EQUIPMENT_MAKES_QUERY,
          variables: {
            first: 500,
            filter: { organizationId: { equal: org.id }, deletedOn: { isNull: true }, unknown: { equal: false } },
          },
        },
        'equipmentMakes'
      );
      return orderBy(equipmentMakes, (cat) => cat.title?.toLowerCase()) as EquipmentMake[];
    } catch (error) {
      throw Error(`Error fetching equipment makes: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const createEquipmentMake = async (createInput: EquipmentMakeCreateInput) => {
    try {
      const result = await client.request<CreateEquipmentMakeMutation, CreateEquipmentMakeMutationVariables>({
        document: CREATE_EQUIPMENT_MAKE_MUTATION,
        variables: { equipmentMake: createInput },
      });

      const newMake = result.createEquipmentMake;

      if (!newMake) {
        throw Error('Failed to create equipment make');
      }

      return newMake;
    } catch (error) {
      throw Error(`Error creating equipment make: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const editEquipmentMake = async (editInput: EquipmentMakeUpdateInput) => {
    try {
      const result = await client.request<UpdateEquipmentMakeMutation, UpdateEquipmentMakeMutationVariables>({
        document: UPDATE_EQUIPMENT_MAKE_MUTATION,
        variables: { equipmentMake: editInput },
      });

      const newMake = result.updateEquipmentMake;

      if (!newMake) {
        throw Error('Failed to update equipment make');
      }

      return newMake;
    } catch (error) {
      throw Error(`Error updating equipment make: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const deleteEquipmentMake = async (editInput: EquipmentMakeUpdateInput) => {
    try {
      const result = await client.request<UpdateEquipmentMakeMutation, UpdateEquipmentMakeMutationVariables>({
        document: UPDATE_EQUIPMENT_MAKE_MUTATION,
        variables: { equipmentMake: editInput },
      });

      const newMake = result.updateEquipmentMake;

      if (!newMake) {
        throw Error('Failed to delete equipment make');
      }

      return newMake;
    } catch (error) {
      throw Error(`Error deleting equipment make: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return { getEquipmentMakes, createEquipmentMake, editEquipmentMake, deleteEquipmentMake };
}

export const CREATE_EQUIPMENT_MAKE_MUTATION = gql`
  mutation CreateEquipmentMake($equipmentMake: EquipmentMakeCreateInput) {
    createEquipmentMake(equipmentMake: $equipmentMake) {
      id
      title
      createdOn
      deletedOn
    }
  }
`;

export const UPDATE_EQUIPMENT_MAKE_MUTATION = gql`
  mutation UpdateEquipmentMake($equipmentMake: EquipmentMakeUpdateInput) {
    updateEquipmentMake(equipmentMake: $equipmentMake) {
      id
      title
      updatedOn
      deletedOn
    }
  }
`;

export const EQUIPMENT_MAKES_QUERY = gql`
  query EquipmentMakes($after: String, $filter: EquipmentMakeFilter, $sort: [EquipmentMakeSort!], $first: Int) {
    equipmentMakes(after: $after, first: $first, filter: $filter, sort: $sort) {
      id
      cursor
      organizationId
      title
      review
      createdOn
      updatedOn
      deletedOn
      submittedOn
    }
  }
`;
